import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { injectRuntimeConfig } from '../core/config/runtime-config.token';
import { LegacyPreferenceStorageService } from './legacy-preference-storage.service';

@Injectable({ providedIn: 'root' })
export class AudioNotificationService {
  private readonly document = inject(DOCUMENT);
  private readonly runtimeConfig = injectRuntimeConfig();
  private readonly audioBySource = new Map<string, HTMLAudioElement>();
  private playbackPrimed = false;
  private pendingAlarm = false;
  private alarmRetryHandler: (() => void) | null = null;

  constructor(private readonly preferenceStorage: LegacyPreferenceStorageService) {}

  playNewOrderNotification(): void {
    if (!this.preferenceStorage.readBoolean('notification', false)) {
      return;
    }

    this.play(this.runtimeConfig.assets.sounds.newOrder);
  }

  playCancelledOrderNotification(): void {
    if (!this.preferenceStorage.readBoolean('notificationCancel', false)) {
      return;
    }

    this.play(this.runtimeConfig.assets.sounds.orderCancelled);
  }

  previewNewOrderNotification(): void {
    this.play(this.runtimeConfig.assets.sounds.newOrder);
  }

  previewCancelledOrderNotification(): void {
    this.play(this.runtimeConfig.assets.sounds.orderCancelled);
  }

  playPreorderAlarmNotification(): void {
    if (typeof Audio === 'undefined') {
      return;
    }

    this.cancelPendingAlarmRetry();
    this.attemptAlarm();
  }

  private attemptAlarm(): void {
    let count = 0;
    const playOnce = () => {
      const audio = new Audio(this.resolveSource('./sounds/notification3.mp3'));
      audio.preload = 'auto';
      void audio.play()
        .then(() => {
          audio.addEventListener('ended', () => {
            if (++count < 3) {
              setTimeout(playOnce, 400);
            }
          });
        })
        .catch(() => {
          this.scheduleAlarmRetryOnInteraction();
        });
    };
    playOnce();
  }

  private scheduleAlarmRetryOnInteraction(): void {
    this.cancelPendingAlarmRetry();
    const handler = () => {
      this.alarmRetryHandler = null;
      setTimeout(() => this.attemptAlarm(), 50);
    };
    this.alarmRetryHandler = handler;
    this.document.addEventListener('pointerdown', handler, { once: true });
  }

  private cancelPendingAlarmRetry(): void {
    if (this.alarmRetryHandler) {
      this.document.removeEventListener('pointerdown', this.alarmRetryHandler);
      this.alarmRetryHandler = null;
    }
  }

  primePlayback(): void {
    if (typeof Audio === 'undefined' || this.playbackPrimed) {
      return;
    }

    const primingAudios = [
      this.getAudio(this.runtimeConfig.assets.sounds.newOrder),
      this.getAudio(this.runtimeConfig.assets.sounds.orderCancelled)
    ];

    for (const audio of primingAudios) {
      try {
        audio.volume = 0;
        const playback = audio.play();

        if (playback && typeof playback.then === 'function') {
          void playback
            .then(() => {
              audio.pause();
              audio.currentTime = 0;
              audio.volume = 1;
              this.playbackPrimed = true;
            })
            .catch(() => {
              audio.volume = 1;
            });
          continue;
        }

        audio.pause();
        audio.currentTime = 0;
        audio.volume = 1;
        this.playbackPrimed = true;
      } catch {
        audio.volume = 1;
      }
    }
  }

  private play(source: string): void {
    if (typeof Audio === 'undefined') {
      return;
    }

    const audio = new Audio(this.resolveSource(source));
    audio.preload = 'auto';
    audio.currentTime = 0;
    void audio.play().catch(() => undefined);
  }

  private getAudio(source: string): HTMLAudioElement {
    const resolvedSource = this.resolveSource(source);
    const existing = this.audioBySource.get(resolvedSource);

    if (existing) {
      return existing;
    }

    const audio = new Audio(resolvedSource);
    audio.preload = 'auto';
    this.audioBySource.set(resolvedSource, audio);
    return audio;
  }

  private resolveSource(source: string): string {
    const baseUri = this.document?.baseURI;

    if (!baseUri) {
      return source;
    }

    try {
      return new URL(source, baseUri).toString();
    } catch {
      return source;
    }
  }
}
